import React, { useState, useEffect, useRef } from 'react';

const CloudSolutionsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const carouselRef = useRef(null);

  const solutions = [
    {
      title: 'Seamless Cloud Migrations',
      description:
        'Migrate to the cloud with confidence. Effortlessly move your workloads to AWS, GCP or Azure with zero downtime. We handle everything from planning to execution.',
      image: '/images/cloud_migration.jpg',
    },
    {
      title: 'DevOps Optimization Services',
      description:
        'Enhance your DevOps processes with our optimization services. Streamline your development and operations with automated CI/CD pipelines, infrastructure as code, and faster release cycles with RogerOps.',
      image: '/images/devoops-optimization.webp',
    },
    {
      title: 'FinOps Expertise (Cloud Cost Optimization)',
      description:
        'Slash unnecessary spend and boost ROI. Our FinOps experts help you gain full visibility and control over your cloud bills.',
      image: '/images/finops.jpg',
    },
    {
      title: 'DevSecOps (Secured Cloud Infrastructure)',
      description:
        'Build and maintain secure cloud environments with industry best practices, compliance-ready frameworks, and proactive threat protection.',
      image: '/images/cloud_security.jpeg',
    },
    {
      title: 'Disaster Recovery Planning',
      description:
        'Protect your business with comprehensive disaster recovery solutions. At RogerOps, we design resilient architectures that ensure business continuity during unexpected events.',
      image: '/images/disaster_recovery.jpg',
    },
    {
      title: 'Cloud Native Kubernetes Solutions (Container Orchestration)',
      description:
        'Deploy, scale, and manage your containerized applications with ease using Kubernetes in the cloud native way — the right way.',
      image: '/images/k8_expertise.webp',
    },
    {
      title: 'Solution Architecting (HLD + LLD)',
      description:
        'From big-picture thinking to fine-grain details, the experts at RogerOps make sure we deliver architecture that\'s scalable, secure, and cloud-native — both HLD & LLD.',
      image: '/images/Solution-Architecture.jpg',
    },
    {
      title: 'Discounted Pricing for AWS',
      description:
        'Get access to exclusive AWS pricing with no compromise on performance. (Coming soon for Azure and GCP!)',
      image: '/images/discounted_pricing.png',
    },
    {
      title: '24/7 Support Services',
      description:
        'Our expert support team is always online — ensuring your infrastructure is up, running, and problem-free around the clock. At RogerOps, we provide a 24/7 monitoring and support team to our partners, ensuring they sleep soundly at night.',
      image: '/images/24-7_support.jpg',
    },
    {
      title: 'Infrastructure Monitoring',
      description:
        'Track every layer of your cloud stack in real-time with advanced monitoring tools and dashboards tailored to your needs. At RogerOps, we understand the importance of proficient, real-time monitoring system and how it can help to find the real-time errors, hence we ensure to provide and set-up advanced monitoring for your infrastructure.',
      image: '/images/infra_monitoring.jpg',
    },
    {
      title: 'Custom Platform Analysis (Analytics)',
      description:
        'Leverage actionable insights from your infrastructure and apps. We turn raw data into decisions with deep analytics and make those insights visible for you on the customize dashboards.',
      image: '/images/platform_analysis.png',
    },
    {
      title: 'MLOps Workflows along with GPUs',
      description:
        'Accelerate your AI/ML workloads with high-performance GPU infrastructure, optimized for scale and speed. At RogerOps, we ensure our partners gets the easy and affordable access to GPUs, empowering their AI workloads.',
      image: '/images/mlops.webp',
    },
    {
      title: 'Robust & Extensive CI/CD Pipelines',
      description:
        'Design and automate robust data pipelines to move, clean, and store data for faster analytics and decision-making.',
      image: '/images/cicd.avif',
    },
    {
      title: 'Data Pipelines (ETL, Warehouse, Data Warehouse)',
      description:
        'Design and automate robust data pipelines to move, clean, and store data for faster analytics and decision-making.',
      image: '/images/etl.png',
    },
  ];

  const maxSlide = solutions.length - 2;
  const minSlide = 1;

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      setIsMobile(mobile);
      
      if (mobile) {
        setItemsPerSlide(1);
        // Enable scrolling on mobile
        setIsScrolling(true);
      } else if (width < 1024) {
        setItemsPerSlide(2);
        setIsScrolling(false);
      } else {
        setItemsPerSlide(3);
        setIsScrolling(false);
      }
      
      // Calculate maximum scroll width for indicators
      if (carouselRef.current) {
        const maxScrollValue = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
        setMaxScroll(maxScrollValue);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Track scroll position for scroll indicators
  useEffect(() => {
    const handleScroll = () => {
      if (carouselRef.current) {
        setScrollPosition(carouselRef.current.scrollLeft);
      }
    };
    
    const carousel = carouselRef.current;
    if (carousel && isScrolling) {
      carousel.addEventListener('scroll', handleScroll);
      // Calculate maximum scroll width
      setMaxScroll(carousel.scrollWidth - carousel.clientWidth);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, [isScrolling]);

  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  const nextSlide = () => {
    console.log("Next button clicked");
    if (!isTransitioning && currentSlide < maxSlide) {
      setIsTransitioning(true);
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    console.log("Prev button clicked");
    if (!isTransitioning && currentSlide > minSlide) {
      setIsTransitioning(true);
      setCurrentSlide(prev => prev - 1);
    }
  };

  // CSS styles for hiding scrollbar while allowing scrolling
  const scrollbarHideStyle = {
    scrollbarWidth: 'none', /* Firefox */
    msOverflowStyle: 'none', /* IE and Edge */
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-5xl text-gray-900 mb-4">
              Unlock Your Business
              <br />
              Potential with Our{' '}
              <span className="italic">
                Cloud
                <br />
                Solutions
              </span>
            </h2>
          </div>
          <div>
            <p className="text-lg text-gray-700">
              At RogerOps, we provide comprehensive cloud solutions tailored to
              meet your unique business needs. From seamless cloud migrations to
              DevOps optimization, our expert team ensures a smooth transition
              and enhanced operational efficiency. Discover how our services can
              transform your business and drive growth.
            </p>
          </div>
        </div>

        <div className="relative">
          {/* Navigation Arrows - Only visible when not scrolling */}
          {!isScrolling && (
            <>
              <button
                onClick={prevSlide}
                disabled={currentSlide === minSlide}
                className={`absolute left-[-3rem] top-1/2 border-2 bg-white p-2 rounded-full shadow-lg z-10 ${
                  currentSlide === minSlide
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-100 focus:outline-none'
                }`}
                aria-label="Previous slide"
              >
                <div className="flex items-center justify-center w-6 h-6">
                  <svg
                    className="text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </div>
              </button>

              <button
                onClick={nextSlide}
                disabled={currentSlide === maxSlide}
                className={`absolute right-[-3rem] top-1/2 bg-white p-2 border-2 rounded-full shadow-lg z-10 ${
                  currentSlide === maxSlide
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-100 focus:outline-none'
                }`}
                aria-label="Next slide"
              >
                <div className="flex items-center justify-center w-6 h-6">
                  <svg
                    className="text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
            </>
          )}

          {/* Carousel container */}
          <div 
            ref={carouselRef}
            className={`overflow-x-auto ${isScrolling ? "" : "overflow-hidden"}`}
            style={isScrolling ? scrollbarHideStyle : {}}
          >
            <div
              className={`flex transition-transform ease-in-out ${
                isTransitioning ? 'duration-500' : 'duration-0'
              }`}
              style={
                isScrolling 
                  ? {} 
                  : {
                      transform: `translateX(-${
                        (currentSlide - 1) * (100 / itemsPerSlide)
                      }%)`,
                    }
              }
            >
              {solutions.map((solution, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 px-2"
                  style={{ width: `${100 / itemsPerSlide}%` }}
                >
                  <div className="bg-blue-50 rounded-lg overflow-hidden h-full">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-64 object-cover"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-[#3498db] mb-3">
                        {solution.title}
                      </h3>
                      <p className="text-gray-700 mb-6" style={{ marginTop: '30px' }}>
                        {solution.description}
                      </p>
                      {/* <button className="px-6 py-2 border border-black text-gray-700 rounded-md hover:bg-black hover:text-white transition-colors">
                        Learn More
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Modern Dot Indicators for scrollable content */}
        {isScrolling && maxScroll > 0 && (
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: Math.ceil(maxScroll / 300) + 1 }).map((_, index) => {
              // Calculate if this dot represents the current visible section
              const sectionStart = index * 300;
              const sectionEnd = (index + 1) * 300;
              const isActive = scrollPosition >= sectionStart && scrollPosition < sectionEnd;
              
              return (
                <div 
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${isActive ? 'w-8 bg-blue-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} 
                  aria-hidden="true"
                  onClick={() => {
                    if (carouselRef.current) {
                      carouselRef.current.scrollTo({
                        left: sectionStart,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                ></div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* CSS to hide WebKit scrollbar */}
      <style jsx>{`
        ::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default CloudSolutionsCarousel;